/**
 * When the guild raids, as the guild sets it: central European wall-clock
 * time. Europe/Berlin follows the CET/CEST switch, so "21:00" stays 21:00 for
 * the guild all year and moves for everyone else when the clocks change.
 */
export const RAID_SCHEDULE = {
  timeZone: 'Europe/Berlin',
  // 0 = Sunday ... 6 = Saturday, in the guild's time zone
  weekdays: [4, 0, 2], // Thursday, Sunday, Tuesday
  // Raid nights used only when needed; the main nights are the rest
  optionalWeekdays: [2], // Tuesday
  start: { hour: 21, minute: 0 },
  durationMinutes: 180, // until 00:00
}

export interface RaidSlot {
  start: Date
  end: Date
  /** A night the guild raids only when needed (Tuesday) */
  optional: boolean
}

/** Minutes the zone is ahead of UTC at a given instant */
function zoneOffsetMinutes(at: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: 'numeric', second: 'numeric',
  }).formatToParts(at)
  const get = (type: string) => Number(parts.find(p => p.type === type)?.value)
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'))
  return Math.round((asUtc - at.getTime()) / 60000)
}

/** The instant a wall-clock time in `timeZone` happens */
function zonedTimeToDate(year: number, month: number, day: number, hour: number, minute: number, timeZone: string): Date {
  const guess = Date.UTC(year, month, day, hour, minute)
  // Twice, so a guess that lands on the other side of a DST switch corrects itself
  let result = guess - zoneOffsetMinutes(new Date(guess), timeZone) * 60000
  result = guess - zoneOffsetMinutes(new Date(result), timeZone) * 60000
  return new Date(result)
}

/**
 * The next raid on each raid night, from `now`, in chronological order. Real
 * dates rather than abstract weekdays, so a visitor far from Europe sees the
 * day it actually is for them (Tuesday 21:00 in Berlin is Wednesday morning
 * in Sydney).
 */
export function upcomingRaidSlots(now = new Date()): RaidSlot[] {
  const { timeZone, weekdays, optionalWeekdays, start, durationMinutes } = RAID_SCHEDULE

  // Today's date as the guild sees it
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone, year: 'numeric', month: 'numeric', day: 'numeric',
  }).formatToParts(now)
  const get = (type: string) => Number(parts.find(p => p.type === type)?.value)
  const today = new Date(Date.UTC(get('year'), get('month') - 1, get('day')))

  const slots: RaidSlot[] = []
  // Eight days covers every weekday, including tonight's raid if it is still
  // on; the first N found are the next N raids
  for (let offset = 0; offset < 8; offset++) {
    const day = new Date(today.getTime() + offset * 86400000)
    if (!weekdays.includes(day.getUTCDay())) continue
    const startsAt = zonedTimeToDate(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate(), start.hour, start.minute, timeZone)
    const endsAt = new Date(startsAt.getTime() + durationMinutes * 60000)
    if (endsAt <= now) continue
    slots.push({ start: startsAt, end: endsAt, optional: optionalWeekdays.includes(day.getUTCDay()) })
  }
  return slots.slice(0, weekdays.length)
}

/** "21:00 – 00:00 CET" as the guild states it, for the small print */
export function guildTimeLabel(now = new Date()): string {
  const { timeZone, start, durationMinutes } = RAID_SCHEDULE
  const abbrev = new Intl.DateTimeFormat('en-GB', { timeZone, timeZoneName: 'short' })
    .formatToParts(now).find(p => p.type === 'timeZoneName')?.value ?? ''
  const pad = (n: number) => String(n).padStart(2, '0')
  const endTotal = (start.hour * 60 + start.minute + durationMinutes) % (24 * 60)
  return `${pad(start.hour)}:${pad(start.minute)} – ${pad(Math.floor(endTotal / 60))}:${pad(endTotal % 60)} ${abbrev}`
}
